/** Figma geometry in frame pixels; the canvas scales as one piece so text stays on the artwork. */

import imgthankyouwideqr from "../../images/appInstall/banner-thankyou-wide-qr.webp";
import imgthankyouwidebadges from "../../images/appInstall/banner-thankyou-wide-badges.webp";
import imgthankyounarrowqr from "../../images/appInstall/banner-thankyou-narrow-qr.webp";
import imgthankyounarrowbadges from "../../images/appInstall/banner-thankyou-narrow-badges.webp";
import imgorderlistwideqr from "../../images/appInstall/banner-orderlist-wide-qr.webp";
import imgorderlistwidebadges from "../../images/appInstall/banner-orderlist-wide-badges.webp";
import imgorderlistnarrowqr from "../../images/appInstall/banner-orderlist-narrow-qr.webp";
import imgorderlistnarrowbadges from "../../images/appInstall/banner-orderlist-narrow-badges.webp";
import imgorderdetailswideqr from "../../images/appInstall/banner-orderdetails-wide-qr.webp";
import imgorderdetailswidebadges from "../../images/appInstall/banner-orderdetails-wide-badges.webp";
import imgorderdetailsnarrowqr from "../../images/appInstall/banner-orderdetails-narrow-qr.webp";
import imgorderdetailsnarrowbadges from "../../images/appInstall/banner-orderdetails-narrow-badges.webp";
import imgplanwideqr from "../../images/appInstall/banner-plan-wide-qr.webp";
import imgplanwidebadges from "../../images/appInstall/banner-plan-wide-badges.webp";
import imgplannarrowqr from "../../images/appInstall/banner-plan-narrow-qr.webp";
import imgplannarrowbadges from "../../images/appInstall/banner-plan-narrow-badges.webp";

export interface FrameBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface FrameText {
  left: number;
  top: number;
  width: number;
  fontSize: number;
  lineHeight: number;
}

export interface BannerFrame {
  id: BannerFrameId;
  width: number;
  height: number;
  image: string;
  pill: FrameBox & { radius: number; fontSize: number };
  brand: FrameText;
  headline: FrameText;
  /** Only the frames that carry body copy: every badges frame and the plan QR frames. */
  body?: FrameText;
  cta: FrameBox & { radius?: number };
  qrTitle?: FrameText;
  qrSubtitle?: FrameText;
  qrPlate?: FrameBox & { radius: number };
  qr?: { left: number; top: number; size: number };
  badges?: [FrameBox, FrameBox];
}

export type BannerGroup = "thankyou" | "orderlist" | "orderdetails" | "plan";

export type BannerFrameId = `${BannerGroup}-${"wide" | "narrow"}-${"qr" | "badges"}`;

export const BANNER_FRAMES: Record<BannerFrameId, BannerFrame> = {
  "thankyou-wide-qr": {
    id: "thankyou-wide-qr",
    width: 1167,
    height: 481.5,
    image: imgthankyouwideqr,
    pill: { left: 55.43, top: 37, width: 79.21, height: 45, radius: 10.1, fontSize: 29.66 },
    brand: { left: 157.79, top: 46.41, width: 171.21, fontSize: 20.86, lineHeight: 27.11 },
    headline: { left: 53.47, top: 96.62, width: 461.28, fontSize: 42.7, lineHeight: 55.12 },
    cta: { left: 55.43, top: 227, width: 495.25, height: 185.1, radius: 14.9 },
    qrTitle: { left: 26.63, top: 62.09, width: 280.65, fontSize: 25.93, lineHeight: 33.7 },
    qrSubtitle: { left: 26.27, top: 101.08, width: 203.9, fontSize: 21.09, lineHeight: 27.42 },
    qrPlate: { left: 324.5, top: 14.35, width: 156.4, height: 156.4, radius: 23.88 },
    qr: { left: 11.48, top: 11.48, size: 133.44 },
  },
  "thankyou-wide-badges": {
    id: "thankyou-wide-badges",
    width: 1167,
    height: 481.5,
    image: imgthankyouwidebadges,
    pill: { left: 55.43, top: 106.5, width: 79.21, height: 45, radius: 10.1, fontSize: 29.66 },
    brand: { left: 157.79, top: 115.91, width: 171.21, fontSize: 20.86, lineHeight: 27.11 },
    headline: { left: 53.6, top: 170.01, width: 429.1, fontSize: 39.72, lineHeight: 51.11 },
    body: { left: 55.17, top: 288.76, width: 566.18, fontSize: 19.99, lineHeight: 25.83 },
    cta: { left: 55.43, top: 358.26, width: 408, height: 56.76 },
    badges: [
      { left: 0, top: 0, width: 196.49, height: 56.76 },
      { left: 211.78, top: 0, width: 196.22, height: 56.76 },
    ],
  },
  "thankyou-narrow-qr": {
    id: "thankyou-narrow-qr",
    width: 334,
    height: 470.5,
    image: imgthankyounarrowqr,
    pill: { left: 85, top: 16, width: 48.23, height: 28, radius: 6.12, fontSize: 17.96 },
    brand: { left: 147.25, top: 22.04, width: 103.85, fontSize: 12.63, lineHeight: 16.41 },
    headline: { left: 38.36, top: 55.89, width: 259.16, fontSize: 23.97, lineHeight: 31.2 },
    cta: { left: 21, top: 134, width: 293, height: 129, radius: 9.88 },
    qrTitle: { left: 17.6, top: 47.56, width: 144.4, fontSize: 13.33, lineHeight: 17.32 },
    qrSubtitle: { left: 17.41, top: 67.34, width: 104.95, fontSize: 10.84, lineHeight: 14.1 },
    qrPlate: { left: 174, top: 10, width: 109, height: 109, radius: 16.64 },
    qr: { left: 8, top: 8, size: 93 },
  },
  "thankyou-narrow-badges": {
    id: "thankyou-narrow-badges",
    width: 334,
    height: 455,
    image: imgthankyounarrowbadges,
    pill: { left: 85, top: 16, width: 48.23, height: 28, radius: 6.12, fontSize: 17.96 },
    brand: { left: 147.25, top: 22.04, width: 103.85, fontSize: 12.63, lineHeight: 16.41 },
    headline: { left: 38.36, top: 55.89, width: 259.16, fontSize: 23.97, lineHeight: 31.2 },
    body: { left: 26.68, top: 133.09, width: 272.83, fontSize: 12.99, lineHeight: 16.95 },
    cta: { left: 15, top: 201, width: 303.88, height: 42.28 },
    badges: [
      { left: 0, top: 0, width: 146.35, height: 42.28 },
      { left: 157.73, top: 0, width: 146.15, height: 42.28 },
    ],
  },
  "orderlist-wide-qr": {
    id: "orderlist-wide-qr",
    width: 747,
    height: 289.5,
    image: imgorderlistwideqr,
    pill: { left: 35.48, top: 16, width: 50.93, height: 29, radius: 6.47, fontSize: 18.98 },
    brand: { left: 101.23, top: 22.32, width: 109.75, fontSize: 13.35, lineHeight: 17.35 },
    headline: { left: 34.59, top: 55.79, width: 340.73, fontSize: 26.07, lineHeight: 34.14 },
    cta: { left: 35.68, top: 136.9, width: 346.23, height: 129, radius: 10.39 },
    qrTitle: { left: 17.97, top: 43.29, width: 197.26, fontSize: 19.23, lineHeight: 24.99 },
    qrSubtitle: { left: 18.3, top: 70.06, width: 142.24, fontSize: 14.7, lineHeight: 19.11 },
    qrPlate: { left: 227.23, top: 10, width: 109, height: 109, radius: 16.65 },
    qr: { left: 8, top: 8, size: 93 },
  },
  "orderlist-wide-badges": {
    id: "orderlist-wide-badges",
    width: 747,
    height: 248.5,
    image: imgorderlistwidebadges,
    pill: { left: 35.48, top: 28, width: 50.93, height: 29, radius: 6.47, fontSize: 18.98 },
    brand: { left: 101.23, top: 34.32, width: 109.75, fontSize: 13.35, lineHeight: 17.35 },
    headline: { left: 34.59, top: 67.79, width: 340.73, fontSize: 26.07, lineHeight: 34.14 },
    body: { left: 35.77, top: 145.02, width: 333.4, fontSize: 12.99, lineHeight: 16.89 },
    cta: { left: 35.48, top: 193.76, width: 282.88, height: 39.36 },
    badges: [
      { left: 0, top: 0, width: 136.24, height: 39.36 },
      { left: 146.83, top: 0, width: 136.05, height: 39.36 },
    ],
  },
  "orderlist-narrow-qr": {
    id: "orderlist-narrow-qr",
    width: 344,
    height: 481,
    image: imgorderlistnarrowqr,
    pill: { left: 87.66, top: 16.18, width: 49.58, height: 28, radius: 6.29, fontSize: 18.47 },
    brand: { left: 151.65, top: 22.37, width: 106.8, fontSize: 12.99, lineHeight: 16.88 },
    headline: { left: 35.28, top: 57.47, width: 274.58, fontSize: 24.66, lineHeight: 32.22 },
    cta: { left: 26, top: 142, width: 293, height: 129, radius: 9.88 },
    qrTitle: { left: 17.1, top: 47.67, width: 144.9, fontSize: 14.12, lineHeight: 18.36 },
    qrSubtitle: { left: 17.34, top: 67.29, width: 104.49, fontSize: 10.79, lineHeight: 14.04 },
    qrPlate: { left: 174, top: 10, width: 109, height: 109, radius: 16.64 },
    qr: { left: 8, top: 8, size: 93 },
  },
  "orderlist-narrow-badges": {
    id: "orderlist-narrow-badges",
    width: 344,
    height: 468,
    image: imgorderlistnarrowbadges,
    pill: { left: 87.66, top: 16.68, width: 49.58, height: 28, radius: 6.29, fontSize: 18.47 },
    brand: { left: 151.65, top: 22.87, width: 106.8, fontSize: 12.99, lineHeight: 16.88 },
    headline: { left: 35.28, top: 57.97, width: 274.58, fontSize: 24.66, lineHeight: 32.22 },
    body: { left: 27.69, top: 136.99, width: 280.59, fontSize: 13.36, lineHeight: 16.95 },
    cta: { left: 15.66, top: 206.97, width: 312.55, height: 43.49 },
    badges: [
      { left: 0, top: 0, width: 150.53, height: 43.49 },
      { left: 162.23, top: 0, width: 150.32, height: 43.49 },
    ],
  },
  "orderdetails-wide-qr": {
    id: "orderdetails-wide-qr",
    width: 747,
    height: 310,
    image: imgorderdetailswideqr,
    pill: { left: 35.48, top: 16, width: 50.93, height: 29, radius: 6.47, fontSize: 18.98 },
    brand: { left: 101.23, top: 22.32, width: 109.75, fontSize: 13.35, lineHeight: 17.35 },
    headline: { left: 34.59, top: 55.81, width: 300.2, fontSize: 26.08, lineHeight: 34.1 },
    cta: { left: 35.68, top: 135.9, width: 346.23, height: 129, radius: 10.39 },
    qrTitle: { left: 17.97, top: 43.29, width: 197.26, fontSize: 19.23, lineHeight: 24.99 },
    qrSubtitle: { left: 18.3, top: 70.06, width: 142.24, fontSize: 14.7, lineHeight: 19.11 },
    qrPlate: { left: 227.23, top: 10, width: 109, height: 109, radius: 16.65 },
    qr: { left: 8, top: 8, size: 93 },
  },
  "orderdetails-wide-badges": {
    id: "orderdetails-wide-badges",
    width: 747,
    height: 310,
    image: imgorderdetailswidebadges,
    pill: { left: 35.48, top: 71.5, width: 50.93, height: 29, radius: 6.47, fontSize: 18.98 },
    brand: { left: 101.23, top: 77.82, width: 109.75, fontSize: 13.35, lineHeight: 17.35 },
    headline: { left: 34.59, top: 111.31, width: 300.2, fontSize: 26.08, lineHeight: 34.1 },
    body: { left: 35.78, top: 188.69, width: 252.17, fontSize: 13.99, lineHeight: 17.87 },
    cta: { left: 35.48, top: 237.26, width: 282.88, height: 39.36 },
    badges: [
      { left: 0, top: 0, width: 136.24, height: 39.36 },
      { left: 146.83, top: 0, width: 136.05, height: 39.36 },
    ],
  },
  "orderdetails-narrow-qr": {
    id: "orderdetails-narrow-qr",
    width: 343.5,
    height: 515,
    image: imgorderdetailsnarrowqr,
    pill: { left: 87.55, top: 16.57, width: 49.58, height: 28, radius: 6.29, fontSize: 18.47 },
    brand: { left: 151.54, top: 22.75, width: 106.8, fontSize: 12.99, lineHeight: 16.88 },
    headline: { left: 30.95, top: 57.87, width: 283.99, fontSize: 24.67, lineHeight: 32.19 },
    cta: { left: 25.89, top: 143.89, width: 293, height: 129, radius: 9.88 },
    qrTitle: { left: 17.1, top: 47.67, width: 144.9, fontSize: 14.12, lineHeight: 18.36 },
    qrSubtitle: { left: 17.34, top: 67.29, width: 104.49, fontSize: 10.79, lineHeight: 14.04 },
    qrPlate: { left: 174, top: 10, width: 109, height: 109, radius: 16.64 },
    qr: { left: 8, top: 8, size: 93 },
  },
  "orderdetails-narrow-badges": {
    id: "orderdetails-narrow-badges",
    width: 343.5,
    height: 467.5,
    image: imgorderdetailsnarrowbadges,
    pill: { left: 87.55, top: 16.57, width: 49.58, height: 28, radius: 6.29, fontSize: 18.47 },
    brand: { left: 151.54, top: 22.75, width: 106.8, fontSize: 12.99, lineHeight: 16.88 },
    headline: { left: 30.95, top: 57.87, width: 283.99, fontSize: 24.67, lineHeight: 32.19 },
    body: { left: 41.69, top: 137.2, width: 252.17, fontSize: 13.99, lineHeight: 17.87 },
    cta: { left: 13.89, top: 197.89, width: 312.56, height: 43.49 },
    badges: [
      { left: 0, top: 0, width: 150.53, height: 43.49 },
      { left: 162.24, top: 0, width: 150.32, height: 43.49 },
    ],
  },
  "plan-wide-qr": {
    id: "plan-wide-qr",
    width: 748,
    height: 327.5,
    image: imgplanwideqr,
    pill: { left: 35.8, top: 16.6, width: 126.93, height: 29, radius: 6.47, fontSize: 19.58 },
    brand: { left: 177.55, top: 22.92, width: 109.75, fontSize: 13.35, lineHeight: 17.35 },
    headline: { left: 36.04, top: 55.53, width: 337.52, fontSize: 25.98, lineHeight: 33.77 },
    body: { left: 36.62, top: 103.71, width: 301.72, fontSize: 14.01, lineHeight: 17.95 },
    cta: { left: 31, top: 177, width: 346.24, height: 129, radius: 10.39 },
    qrTitle: { left: 17.98, top: 43.29, width: 197.26, fontSize: 19.23, lineHeight: 24.99 },
    qrSubtitle: { left: 18.31, top: 70.06, width: 142.24, fontSize: 14.7, lineHeight: 19.11 },
    qrPlate: { left: 227.24, top: 10, width: 109, height: 109, radius: 16.65 },
    qr: { left: 8, top: 8, size: 93 },
  },
  "plan-wide-badges": {
    id: "plan-wide-badges",
    width: 747,
    height: 248.5,
    image: imgplanwidebadges,
    pill: { left: 35.48, top: 29, width: 126.93, height: 29, radius: 6.47, fontSize: 19.58 },
    brand: { left: 177.23, top: 35.32, width: 109.75, fontSize: 13.35, lineHeight: 17.35 },
    headline: { left: 35.71, top: 67.93, width: 337.52, fontSize: 25.98, lineHeight: 33.77 },
    body: { left: 36.3, top: 116.11, width: 301.72, fontSize: 14.01, lineHeight: 17.95 },
    cta: { left: 35.48, top: 193.76, width: 282.88, height: 39.36 },
    badges: [
      { left: 0, top: 0, width: 136.24, height: 39.36 },
      { left: 146.83, top: 0, width: 136.05, height: 39.36 },
    ],
  },
  "plan-narrow-qr": {
    id: "plan-narrow-qr",
    width: 344,
    height: 584,
    image: imgplannarrowqr,
    pill: { left: 50.66, top: 16.69, width: 123.58, height: 28, radius: 6.29, fontSize: 19.05 },
    brand: { left: 188.65, top: 22.87, width: 106.8, fontSize: 12.99, lineHeight: 16.88 },
    headline: { left: 78.46, top: 58.02, width: 189.01, fontSize: 24.64, lineHeight: 32.11 },
    body: { left: 21.42, top: 137.2, width: 301.24, fontSize: 13.99, lineHeight: 17.96 },
    cta: { left: 25, top: 203, width: 293, height: 129, radius: 9.88 },
    qrTitle: { left: 17.1, top: 47.67, width: 144.9, fontSize: 14.12, lineHeight: 18.36 },
    qrSubtitle: { left: 17.34, top: 67.29, width: 104.49, fontSize: 10.79, lineHeight: 14.04 },
    qrPlate: { left: 174, top: 10, width: 109, height: 109, radius: 16.64 },
    qr: { left: 8, top: 8, size: 93 },
  },
  "plan-narrow-badges": {
    id: "plan-narrow-badges",
    width: 344,
    height: 468,
    image: imgplannarrowbadges,
    pill: { left: 50.66, top: 16.69, width: 123.58, height: 28, radius: 6.29, fontSize: 19.05 },
    brand: { left: 188.65, top: 22.87, width: 106.8, fontSize: 12.99, lineHeight: 16.88 },
    headline: { left: 78.46, top: 58.02, width: 189.01, fontSize: 24.64, lineHeight: 32.11 },
    body: { left: 32.8, top: 137.22, width: 270.52, fontSize: 13.99, lineHeight: 17.98 },
    cta: { left: 16, top: 230, width: 312.56, height: 43.49 },
    badges: [
      { left: 0, top: 0, width: 150.53, height: 43.49 },
      { left: 162.24, top: 0, width: 150.32, height: 43.49 },
    ],
  },
};

/** The placements that share a set of frames. W6 and W7 are not banners. */
const GROUP_BY_PLACEMENT: Partial<Record<string, BannerGroup>> = {
  W1: "thankyou",
  W2: "thankyou",
  W3: "orderlist",
  W4: "orderlist",
  W5: "orderdetails",
  W8: "plan",
};

export const getBannerFrame = (placementId: string, isNarrow: boolean, prefersQr: boolean): BannerFrame | undefined => {
  const group = GROUP_BY_PLACEMENT[placementId];
  if (!group) return undefined;
  return BANNER_FRAMES[`${group}-${isNarrow ? "narrow" : "wide"}-${prefersQr ? "qr" : "badges"}`];
};
